import React from 'react';
import Link from 'next/link';
import style from './navigation.module.scss';
import { replaceAttrSpaceWithHyphen } from '../../../utils/string-helpers/replace-attr-space-with-hyphen';
import IMenuItem from '../../../models/header/IMenuItem';
import { ChevronDown } from '../../atoms/icons/icons';
import colorStyle from '../../../styles/utils/variables.module.scss';

type NavigationItemProps = {
  items: IMenuItem[];
  level: number;
  parentId: string;
  openMenus: Set<string>;
  pathname: string;
  toggleMenu: (id: string, isExpanded: boolean, parentId?: string) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>, id: string, parentId?: string) => void;
  translate: (key: string) => string;
  closeAllMenus: () => void;
};

const NavigationItem: React.FC<NavigationItemProps> = ({
  items,
  level,
  parentId,
  openMenus,
  pathname,
  toggleMenu,
  handleKeyDown,
  translate,
  closeAllMenus,
}) => (
  <ul
    className={`${style['navigation__nav__list']} menu-level-${level} p-0 m-0`}
    role='menu'
    aria-label={`Submenu level ${level}`}
  >
    {items.map((item) => {
      const menuId = parentId
        ? `${parentId}-${replaceAttrSpaceWithHyphen(item.text || translate(item.translationKey))}`
        : replaceAttrSpaceWithHyphen(item.text || translate(item.translationKey));
      const isExpanded = openMenus.has(menuId);

      return (
        <li
          key={`${menuId}--${item.text || translate(item.translationKey)}`}
          className={`${style['navigation__nav__item']} ${
            pathname !== '/' && item.url && item.url.includes(pathname)
              ? style['navigation__nav__item--highlighted']
              : ''
          }`}
          role='menuitem'
          onClick={item.children ? null : closeAllMenus} // to close sub menu items when clicking on a link
        >
          {item.url ? (
            <Link href={item.url} className={style['navigation__nav__link']}>
              {item.text || translate(item.translationKey)}
            </Link>
          ) : (
            <button
              aria-expanded={isExpanded}
              aria-controls={`${menuId}-menu`}
              id={`${menuId}-menu-button`}
              onClick={() => toggleMenu(menuId, isExpanded, parentId)}
              onKeyDown={(e) => handleKeyDown(e, menuId, parentId)}
              className={`${style['navigation__button']} ${
                isExpanded ? style['navigation__button--open'] : style['navigation__button--close']
              }`}
              data-id={menuId}
              data-parent-id={parentId}
            >
              <div className={style['navigation__button__container']}>
                <span className={style['navigation__button__text']}>
                  {item.text || translate(item.translationKey)}
                </span>
                <span className={style['navigation__button__chevron']}>
                  <ChevronDown fillColor={colorStyle.white} height={18} width={16} />
                </span>
              </div>
            </button>
          )}
          {item.children && (
            <div
              className={`${style['navigation__submenu']} ${
                isExpanded
                  ? style['navigation__submenu--open']
                  : style['navigation__submenu--closed']
              }`}
              id={`${menuId}-menu`}
              role='menu'
              aria-hidden={!isExpanded}
              data-parent-id={menuId}
            >
              <NavigationItem
                items={item.children}
                level={level + 1}
                parentId={menuId}
                openMenus={openMenus}
                pathname={pathname}
                toggleMenu={toggleMenu}
                handleKeyDown={handleKeyDown}
                translate={translate}
                closeAllMenus={closeAllMenus}
              />
            </div>
          )}
        </li>
      );
    })}
  </ul>
);

export default NavigationItem;
